import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, FileDown, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";

function fmt(d) {
  try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return d; }
}

export default function ParticipantDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = () => {
    Promise.all([
      api.get(`/admin/participants/${id}`),
      api.get(`/admin/participants/${id}/sessions`),
    ])
      .then(([pr, sr]) => {
        setP(pr.data);
        setSessions(sr.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const resetPassword = async () => {
    if (!window.confirm(`Reset password for ${p.name}? A new temporary password will be generated.`)) return;
    try {
      const r = await api.post(`/admin/participants/${id}/reset-password`, {});
      toast.success(`New temporary password: ${r.data.temp_password}`, { duration: 9000 });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadPdf = async (sid) => {    setDownloadingId(sid);
    try {
      const res = await api.get(`/sessions/${sid}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leadersim-report-${sid.slice(-6)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-12 text-stone-500 text-sm">Loading…</div>
      </div>
    );
  }
  if (!p) return null;

  const completed = sessions.filter((s) => s.status === "completed" && s.overall_score != null);
  const avg = completed.length ? Math.round(completed.reduce((a, s) => a + s.overall_score, 0) / completed.length) : null;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <button onClick={() => window.history.back()} className="inline-flex items-center text-sm text-stone-500 hover:text-stone-800 mb-5">
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Back
        </button>

        <div className="bg-white border border-stone-200 rounded-3xl p-7 mb-6">
          <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-2">Participant</div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-3xl text-stone-900 tracking-tight">{p.name}</h1>
              <div className="text-sm text-stone-500 mt-1">{p.email}</div>
            </div>
            <Button
              data-testid="reset-password-btn"
              variant="outline"
              onClick={resetPassword}
              className="rounded-md h-9 border-stone-300"
            >
              <KeyRound className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Reset password
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">Employee ID</div>
              <div className="text-stone-900 mt-0.5 font-mono text-xs">{p.employee_id || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">Department</div>
              <div className="text-stone-900 mt-0.5">{p.department || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">Designation</div>
              <div className="text-stone-900 mt-0.5">{p.designation || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">Avg score</div>
              <div className="font-display text-2xl text-stone-900 mt-0.5">{avg ?? "—"}{avg && <span className="text-stone-400 text-sm ml-1">/100</span>}</div>
            </div>
          </div>
        </div>

        <h2 className="font-display text-xl text-stone-900 mb-3">Coaching sessions</h2>
        {sessions.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-10 text-center text-stone-500">
            This participant hasn&apos;t started any role-plays yet.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="participant-sessions">
            {sessions.map((s) => (
              <li key={s.id} className="flex items-center gap-3 bg-white border border-stone-200 rounded-2xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">{s.scenario_category}</div>
                  <div className="font-medium text-stone-900 truncate">{s.scenario_title}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{fmt(s.started_at)}</div>
                </div>
                <div className="text-right">
                  {s.status === "completed" && s.overall_score != null ? (
                    <>
                      <div className="font-display text-xl text-stone-900">{s.overall_score}</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">score</div>
                    </>
                  ) : (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">{s.status}</span>
                  )}
                </div>
                {s.status === "completed" && s.overall_score != null && (
                  <Button
                    data-testid={`pdf-${s.id}-btn`}
                    variant="outline"
                    onClick={() => downloadPdf(s.id)}
                    disabled={downloadingId === s.id}
                    className="rounded-md h-9 border-stone-300"
                  >
                    <FileDown className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                    {downloadingId === s.id ? "…" : "PDF"}
                  </Button>
                )}
                <Link to={`/session/${s.id}/review`} className="ml-1 text-stone-400 hover:text-stone-700">
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
